<template>
  <div
    v-if="visibleTasks.length > 0 || tasksStore.isLoading || (isFull && tasksStore.loaded)"
    class="min-w-0 overflow-hidden rounded-lg border bg-card"
  >
    <div class="flex items-center justify-between border-b px-4 py-3.5 md:px-[22px] md:py-[18px]">
      <div class="min-w-0">
        <h2 class="text-base font-bold tracking-tight">{{ t("pages.subtitleTasks.listTitle") }}</h2>
        <p class="mt-0.5 text-[13px] font-normal text-muted-foreground">{{ t("pages.subtitleTasks.listDescription") }}</p>
      </div>
      <NuxtLink v-if="showViewAll" to="/subtitle-tasks" class="shrink-0 text-[13px] font-semibold text-primary">
        {{ t("pages.subtitleTasks.viewAll") }}
      </NuxtLink>
    </div>

    <!-- Announces ready/failed transitions picked up by polling -->
    <div class="sr-only" aria-live="polite" aria-atomic="true">{{ liveMessage }}</div>

    <div class="py-1.5">
      <template v-if="isInitialLoading">
        <div
          v-for="n in 3"
          :key="`skeleton-${n}`"
          class="flex flex-col gap-2.5 border-b px-4 py-3 last:border-b-0 md:flex-row md:items-center md:gap-4 md:px-[22px] md:py-3.5"
          aria-hidden="true"
        >
          <div class="flex min-w-0 items-center gap-3 md:flex-1">
            <Skeleton class="h-9 w-9 flex-none rounded-sm" />
            <div class="min-w-0 space-y-1.5">
              <Skeleton class="h-3.5 w-40 max-w-full" />
              <Skeleton class="h-3 w-28 max-w-full" />
            </div>
          </div>
          <div class="flex items-center justify-between gap-2 md:justify-end md:gap-4">
            <Skeleton class="h-6 w-20 rounded-full" />
            <Skeleton class="h-8 w-[70px] rounded-md" />
          </div>
        </div>
      </template>

      <div
        v-for="task in visibleTasks"
        :key="task.jobId"
        class="flex flex-col gap-2.5 border-b px-4 py-3 last:border-b-0 md:flex-row md:items-center md:gap-4 md:px-[22px] md:py-3.5"
      >
        <div class="flex min-w-0 items-center gap-3 md:flex-1">
          <span
            class="grid h-9 w-9 flex-none place-items-center rounded-sm border bg-muted/60 text-primary"
            aria-hidden="true"
          >
            <Icon :name="taskIcon(task)" class="h-[17px] w-[17px]" />
          </span>
          <div class="min-w-0">
            <b :id="`task-title-${task.jobId}`" class="block truncate text-sm font-semibold" :title="taskTitle(task)">{{ taskTitle(task) }}</b>
            <span class="text-[12.5px] text-subtle-foreground">
              <template v-if="task.tool === 'summary' || task.tool === 'translate' || task.tool === 'ocr'"><span class="font-semibold text-primary">{{ t(`pages.subtitleTasks.kind.${task.tool}`) }}</span> · </template><template v-if="task.targetLanguage">{{ languageLabel(task.targetLanguage) }} · </template>{{ formatDateTime(task.createdAt, locale) }}
            </span>
            <div v-if="displayStatus(task) === 'processing' && task.fetchPhase" class="mt-1.5 max-w-[240px]">
              <!-- rtl flip: the shimmer travels toward the reading end -->
              <div
                class="h-1.5 overflow-hidden rounded-full bg-muted rtl:-scale-x-100"
                role="progressbar"
                aria-valuemin="0"
                aria-valuemax="100"
                :aria-label="`${taskTitle(task)}: ${statusLabel(task)}`"
              >
                <div class="h-full w-2/5 animate-shimmer rounded-full bg-primary/70 motion-reduce:w-full motion-reduce:animate-none motion-reduce:opacity-40"></div>
              </div>
            </div>
            <div v-else-if="displayStatus(task) === 'processing' && progressOf(task) !== null" class="mt-1.5 max-w-[240px]">
              <div
                class="h-1.5 overflow-hidden rounded-full bg-muted"
                role="progressbar"
                aria-valuemin="0"
                aria-valuemax="100"
                :aria-valuenow="progressOf(task) ?? 0"
                :aria-label="`${taskTitle(task)}: ${statusLabel(task)}`"
              >
                <div class="h-full rounded-full bg-primary transition-[width] duration-300" :style="{ width: `${progressOf(task) ?? 0}%` }"></div>
              </div>
            </div>
            <p v-if="displayStatus(task) === 'failed' && task.errorMessage" class="mt-0.5 text-[12.5px] text-destructive">
              {{ displayErrorMessage(task) }}
            </p>
          </div>
        </div>

        <div class="flex items-center justify-between gap-2 md:justify-end md:gap-4">
          <span
            class="inline-flex w-fit items-center gap-1.5 whitespace-nowrap rounded-full px-[11px] py-1 text-xs font-semibold"
            :class="isExpiredReady(task) ? idleStatusClass : statusBadgeClass(task)"
          >
            <i class="h-1.5 w-1.5 rounded-full bg-current"></i>{{ isExpiredReady(task) ? t("pages.subtitleTasks.status.expired") : statusLabel(task) }}
          </span>

          <div class="flex shrink-0 items-center gap-2">
          <span v-if="isExpiredReady(task)" class="whitespace-nowrap text-[12.5px] text-subtle-foreground">
            {{ t("pages.subtitleTasks.expiredNote") }}
          </span>
          <DropdownMenu v-else-if="displayStatus(task) === 'ready'">
            <DropdownMenuTrigger
              :class="[fmtPillClass, 'border-primary/50 bg-primary/10']"
              :disabled="downloadingId === task.jobId"
              :aria-describedby="`task-title-${task.jobId}`"
              :aria-busy="downloadingId === task.jobId"
            >
              <Icon
                :name="downloadingId === task.jobId ? 'lucide:loader-circle' : 'lucide:file-down'"
                class="h-[13px] w-[13px]"
                aria-hidden="true"
                :class="{ 'animate-spin': downloadingId === task.jobId }"
              />
              {{ t("pages.subtitleTasks.download") }}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" class="min-w-[7rem]">
              <DropdownMenuItem
                v-for="fmt in (['srt', 'vtt', 'txt'] as const)"
                :key="fmt"
                class="cursor-pointer font-mono text-[12.5px] font-semibold tracking-wide"
                @click="download(task, fmt)"
              >{{ fmt.toUpperCase() }}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <button
            v-if="isUnpaid(task)"
            type="button"
            :class="[fmtPillClass, 'border-primary/50 bg-primary/10']"
            :disabled="claimingId === task.jobId"
            :aria-describedby="`task-title-${task.jobId}`"
            :aria-busy="claimingId === task.jobId"
            @click="claimResult(task)"
          >
            <Icon
              :name="claimingId === task.jobId ? 'lucide:loader-circle' : 'lucide:refresh-cw'"
              class="h-[13px] w-[13px]"
              aria-hidden="true"
              :class="{ 'animate-spin': claimingId === task.jobId }"
            />
            {{ t("pages.subtitleTasks.getResult") }}
          </button>
          <button v-if="!isExpiredReady(task)" type="button" :class="fmtPillClass" :aria-describedby="`task-title-${task.jobId}`" @click="open(task)">
            <Icon name="lucide:external-link" class="h-[13px] w-[13px]" aria-hidden="true" />
            {{ t("pages.subtitleTasks.open") }}
          </button>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="isFull && tasksStore.loaded && !tasksStore.isLoading && visibleTasks.length === 0"
      class="px-1 py-[26px] text-center text-[13.5px] text-subtle-foreground"
    >
      <Icon name="lucide:inbox" class="mx-auto mb-2 block h-6 w-6" />
      <p>{{ t("pages.subtitleTasks.empty") }}</p>
      <NuxtLink to="/video-to-subtitle" class="font-semibold text-primary">{{ t("pages.subtitleTasks.emptyCta") }}</NuxtLink>
    </div>

    <div
      v-if="(isFull && tasksStore.hasMore) || visibleTasks.length > 0"
      class="px-4 pb-4"
    >
      <Button
        v-if="isFull && tasksStore.hasMore"
        variant="soft"
        class="mt-4 w-full"
        :disabled="tasksStore.isLoadingMore"
        @click="tasksStore.loadMore()"
      >
        <Icon v-if="tasksStore.isLoadingMore" name="lucide:loader-circle" class="me-2 h-4 w-4 animate-spin" />
        <span>{{ t("pages.subtitleTasks.loadMore") }}</span>
      </Button>
      <div v-if="visibleTasks.length > 0" class="mt-4 flex items-center gap-2 text-[13px] text-subtle-foreground">
        <Icon name="lucide:clock" class="h-[15px] w-[15px] flex-none" />
        <span>{{ t("pages.subtitleTasks.retentionNote", { days: SUBTITLE_TASK_RETENTION_DAYS }) }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { toast } from "vue-sonner";
import { useSubtitleTasks } from "~/store/useSubtitleTasks";
import type { JobSnapshot, SubtitleTaskItem } from "~/types/media-subtitle";
import type { ClientCredits } from "~/composables/useUser";
import {
  SUBTITLE_TASK_RETENTION_DAYS,
  buildDerivedOutputTitle,
  buildMaxSourceFileSizeLabel,
  deriveTaskDisplayStatus,
  deriveTaskProgress,
  deriveTranslateTaskPercent,
  isTaskExpired,
} from "~/utils/media-subtitle-workflow";
import { getSubtitleMimeType, type SubtitleFormat } from "~/utils/subtitle-format";
import { formatDateTime } from "~/utils/tools";
import { fetchJobSubtitle, saveBlob } from "~/utils/media-subtitle-download";
import { AUTO_TARGET_LANGUAGE, GOOGLE_TRANSLATE_LANGUAGES } from "~/utils/languages";

// announce: off when the host page already voices job status (e.g. the workflow), so nothing is read twice
const props = withDefaults(defineProps<{ limit?: number; showViewAll?: boolean; announce?: boolean }>(), {
  limit: 5,
  showViewAll: true,
  announce: true,
});

const { t, locale } = useI18n();
const tasksStore = useSubtitleTasks();

const isFull = computed(() => props.limit === 0);

const visibleTasks = computed(() =>
  props.limit > 0 ? tasksStore.tasks.slice(0, props.limit) : tasksStore.tasks
);

/** First load only: card is up but there are no rows yet to show. */
const isInitialLoading = computed(() => tasksStore.isLoading && visibleTasks.value.length === 0);

const downloadingId = ref("");
const claimingId = ref("");

onMounted(() => {
  if (!tasksStore.loaded && !tasksStore.isLoading) {
    void tasksStore.fetchTasks();
  } else {
    tasksStore.ensurePolling();
  }
});

function displayStatus(task: SubtitleTaskItem) {
  const status = deriveTaskDisplayStatus(task.platformStatus, task.localStatus);
  // Still "in flight" past retention: a dead job that is no longer polled.
  if ((status === "uploading" || status === "processing") && isTaskExpired(task.createdAt)) {
    return "failed";
  }
  return status;
}

/** A finished task whose result is past the retention window (no longer downloadable). */
function isExpiredReady(task: SubtitleTaskItem) {
  return displayStatus(task) === "ready" && isTaskExpired(task.createdAt);
}

function progressOf(task: SubtitleTaskItem) {
  if (task.tool === "translate" && displayStatus(task) === "processing") {
    return deriveTranslateTaskPercent(task.translateProgress);
  }
  return deriveTaskProgress(task.platformStatus, task.translateProgress);
}

function statusLabel(task: SubtitleTaskItem) {
  return t(`pages.subtitleTasks.status.${displayStatus(task)}`);
}

function taskTitle(task: SubtitleTaskItem) {
  return task.outputTitle || task.fileName || t("pages.subtitleTasks.untitled");
}

// Screen-reader announcement for tasks that finish while the list is open.
// Only transitions are announced: tasks seen for the first time are recorded silently.
const liveMessage = ref("");
const previousStatuses = new Map<string, string>();
watch(
  () => visibleTasks.value.map((task) => [task.jobId, displayStatus(task)] as const),
  (entries) => {
    const messages: string[] = [];
    for (const [jobId, status] of entries) {
      const previous = previousStatuses.get(jobId);
      previousStatuses.set(jobId, status);
      if (previous && previous !== status && (status === "ready" || status === "failed")) {
        const task = visibleTasks.value.find((item) => item.jobId === jobId);
        if (task) messages.push(`${taskTitle(task)}: ${statusLabel(task)}`);
      }
    }
    if (messages.length && props.announce) {
      // Clear first so an identical message (e.g. two "Untitled: Ready") is still announced
      liveMessage.value = "";
      const text = messages.join(". ");
      nextTick(() => (liveMessage.value = text));
    }
  },
  { immediate: true }
);

// Platform error messages are stored in English; localize the known ones.
function displayErrorMessage(task: SubtitleTaskItem) {
  const message = `${task.errorMessage || ""}`;
  if (message.includes("No speech was detected") || message.includes("NO_SPEECH_DETECTED")) {
    return t("pages.audioToSubtitle.errors.noSpeechDetected");
  }
  if (
    message.includes("too large to process") ||
    message.includes("FILE_TOO_LARGE") ||
    message.includes("This media file is too large")
  ) {
    return t("pages.audioToSubtitle.errors.fileTooLarge", { size: buildMaxSourceFileSizeLabel() });
  }
  if (message.includes("cannot be downloaded") || message.includes("SOURCE_UNAVAILABLE")) {
    return t("pages.audioToSubtitle.errors.sourceUnavailable");
  }
  // Unpaid result (INSUFFICIENT_CREDITS_MESSAGE / OUTPUT_EXPIRED_MESSAGE).
  if (message.startsWith("Not enough credits")) {
    return t("pages.subtitleTasks.unpaidError", { days: SUBTITLE_TASK_RETENTION_DAYS });
  }
  if (message.startsWith("This result has expired")) {
    return t("pages.subtitleTasks.outputExpiredError");
  }
  // Job stopped once its measured length cost more than the balance.
  if (message.startsWith("Stopped early.")) {
    return t("pages.audioToSubtitle.errors.notEnoughCredits");
  }
  // Stuck job closed by the server (STUCK_JOB_RELEASE_MESSAGE and friends).
  if (message.startsWith("Auto-released")) {
    return t("pages.subtitleTasks.stalledError");
  }
  return message;
}

/* Status pill: shared base class + per-status color class. */
const fmtPillClass =
  "inline-flex min-w-[70px] items-center justify-center gap-1.5 rounded-md border border-primary/40 px-3 py-[7px] text-[12.5px] font-semibold text-primary transition hover:-translate-y-px hover:bg-primary hover:text-primary-foreground active:translate-y-0 disabled:pointer-events-none disabled:opacity-55 [@media(pointer:coarse)]:min-h-11";
const idleStatusClass = "bg-muted text-subtle-foreground";

function statusBadgeClass(task: SubtitleTaskItem) {
  switch (displayStatus(task)) {
    case "ready":
      return "bg-primary/10 text-primary";
    case "failed":
      return "bg-destructive/10 text-destructive";
    case "uploading":
    case "processing":
      return "bg-warning/15 text-warning";
    default:
      return idleStatusClass;
  }
}

// Language names in the UI locale; the English list is the fallback.
const displayNamesCache = new Map<string, Intl.DisplayNames | null>();
function languageDisplayNames(lang: string) {
  if (!displayNamesCache.has(lang)) {
    let names: Intl.DisplayNames | null = null;
    try {
      names = new Intl.DisplayNames([lang], { type: "language" });
    } catch {
      // Unsupported locale / runtime: fall back to the English list.
    }
    displayNamesCache.set(lang, names);
  }
  return displayNamesCache.get(lang) ?? null;
}

function languageLabel(code: string) {
  if (code === AUTO_TARGET_LANGUAGE) return t("pages.audioToSubtitle.targetLanguageAuto");
  const fallback = (GOOGLE_TRANSLATE_LANGUAGES as Record<string, string>)[code] || code;
  try {
    // of() echoes unknown codes back (e.g. legacy "iw"/"jw"), so treat that as a miss
    const name = languageDisplayNames(locale.value)?.of(code);
    return name && name !== code ? name : fallback;
  } catch {
    return fallback;
  }
}

function taskIcon(task: SubtitleTaskItem) {
  if (task.tool === "summary") {
    return "lucide:file-text";
  }
  if (task.tool === "translate") {
    return "lucide:languages";
  }
  if (task.tool === "ocr") {
    return "lucide:scan-text";
  }
  return task.mediaKind === "video" ? "lucide:clapperboard" : "lucide:file-audio";
}

function open(task: SubtitleTaskItem) {
  // Summary jobs reopen in the summary page (stored summary); AI translate
  // jobs in the translator page (result preview + download); hardsub OCR
  // jobs in their own page.
  const page =
    task.tool === "summary"
      ? "/video-audio-to-summary"
      : task.tool === "translate"
      ? "/subtitle-translator"
      : task.tool === "ocr"
      ? "/hardsub-to-subtitle"
      : "/video-to-subtitle";
  return navigateTo(`${page}?job=${encodeURIComponent(task.jobId)}`);
}

/** Finished (or still running) but left unpaid for lack of credits. */
function isUnpaid(task: SubtitleTaskItem) {
  return (
    displayStatus(task) === "failed" &&
    `${task.errorMessage || ""}`.startsWith("Not enough credits") &&
    !isTaskExpired(task.createdAt)
  );
}

/** Re-check an unpaid task: the server charges it now if the balance covers it. */
async function claimResult(task: SubtitleTaskItem) {
  if (claimingId.value) {
    return;
  }
  claimingId.value = task.jobId;
  const { applyCredits } = useUser();
  try {
    const res = await $fetch<{ job: JobSnapshot; credits?: ClientCredits }>(
      "/api/video-to-subtitle/platform/status",
      { params: { job: task.jobId } }
    );
    applyCredits(res.credits);
    tasksStore.upsertFromSnapshot(res.job);
    if (res.job.error?.code === "INSUFFICIENT_CREDITS") {
      toast.error(t("pages.subtitleTasks.getResultNeedCredits"));
    }
  } catch {
    toast.error(t("pages.subtitleTasks.downloadFailed"));
  } finally {
    claimingId.value = "";
  }
}

async function download(task: SubtitleTaskItem, fmt: SubtitleFormat) {
  if (downloadingId.value) {
    return;
  }
  if (isTaskExpired(task.createdAt)) {
    toast.error(t("pages.subtitleTasks.expiredNote"));
    return;
  }
  downloadingId.value = task.jobId;
  try {
    const converted = await fetchJobSubtitle(task.jobId, fmt);
    if (!converted) {
      toast.error(t("pages.subtitleTasks.downloadEmpty"));
      return;
    }

    const fileName = `${buildDerivedOutputTitle({
      snapshotTitle: task.outputTitle,
      sourceFileName: task.fileName,
    })}.${fmt}`;
    saveBlob(new Blob([converted], { type: getSubtitleMimeType(fmt) }), fileName);
  } catch {
    toast.error(t("pages.subtitleTasks.downloadFailed"));
  } finally {
    downloadingId.value = "";
  }
}
</script>
